/**
 * "Install app" for the account menu. The menu item triggers the browser's install dialog where
 * one exists; otherwise it asks the parent to show InstallInstructionsDialog, which must be
 * rendered outside the dropdown so it survives the menu closing.
 */
import { Download } from "lucide-react";

import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useInstallPrompt, type InstallMethod } from "@/hooks/use-install-prompt";

interface InstallMenuItemProps {
  onShowInstructions: (method: InstallMethod) => void;
}

export function InstallMenuItem({ onShowInstructions }: InstallMenuItemProps) {
  const { installed, method, promptInstall } = useInstallPrompt();
  if (installed) return null;

  return (
    <DropdownMenuItem
      onSelect={() => {
        if (method === "prompt") {
          void promptInstall();
        } else {
          // Let the menu finish closing before the dialog opens, so focus and pointer events settle.
          setTimeout(() => onShowInstructions(method), 0);
        }
      }}
    >
      <Download aria-hidden /> Install app
    </DropdownMenuItem>
  );
}

interface InstallInstructionsDialogProps {
  method: InstallMethod | null;
  onClose: () => void;
}

export function InstallInstructionsDialog({ method, onClose }: InstallInstructionsDialogProps) {
  return (
    <Dialog open={method !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">Install Tables</DialogTitle>
          <DialogDescription>Adds Tables to your home screen so it opens like an app.</DialogDescription>
        </DialogHeader>
        {method === "ios" ? (
          <ol className="list-decimal space-y-1.5 pl-5 text-[13.5px]">
            <li>Tap the Share button at the bottom of Safari.</li>
            <li>Scroll down and tap "Add to Home Screen".</li>
            <li>Tap "Add".</li>
          </ol>
        ) : (
          <p className="text-[13.5px]">
            Open your browser's menu and choose "Install app" or "Add to Home Screen". In Chrome and Edge the option is
            in the menu at the top right of the window.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
